import { FC, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { formatSum } from '../../../../../utils'
import { AMOUNT_INPUT_PATTERN, parseAmountInput } from '../../../../common/core/amountInput'
import { changeCreditedAmount, previewCreditedAmount } from '../../core/api'
import {
  settlementErrorFromBody,
  settlementErrorFromException,
  settlementErrorText,
} from '../../core/errors'
import { RevisionPreview } from '../../core/response.types'
import { SettlementPreview } from '../SettlementPreview/SettlementPreview'

interface Props {
  show: boolean
  token: string
  currentAmount: number | null
  onClose: () => void
  // The amount the deposit is credited now.
  onChanged: (newAmount: number) => void
}

// «Изменить сумму зачисления»: the operator states what the merchant should have been credited; the
// storno or upward revision is derived on the server and shown before it is posted.
export const ChangeCreditedAmountModal: FC<Props> = ({
  show,
  token,
  currentAmount,
  onClose,
  onChanged,
}) => {
  const [amount, setAmount] = useState('')
  const [comment, setComment] = useState('')
  const [preview, setPreview] = useState<RevisionPreview | null>(null)
  const [loading, setLoading] = useState<'preview' | 'apply' | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (show) {
      setAmount('')
      setComment('')
      setPreview(null)
      setError(null)
    }
  }, [show])

  const body = () => ({ paidAmount: parseAmountInput(amount) ?? 0, comment: comment.trim() })
  const filled = (parseAmountInput(amount) ?? 0) > 0 && comment.trim() !== ''

  const edit = (apply: () => void) => {
    apply()
    setPreview(null)
    setError(null)
  }

  const showPreview = async () => {
    setLoading('preview')
    setError(null)
    try {
      const r = await previewCreditedAmount(token, body())
      if (!r.data.success) {
        setError(settlementErrorFromBody(r.data, 'Не удалось рассчитать проводки'))
        return
      }
      setPreview(r.data.data)
      if (!r.data.data.canApply) {
        setError(
          settlementErrorText(r.data.data.errorCode ?? undefined, 'Операцию провести нельзя')
        )
      }
    } catch (e: unknown) {
      setError(settlementErrorFromException(e, 'Не удалось рассчитать проводки'))
    } finally {
      setLoading(null)
    }
  }

  const apply = async () => {
    setLoading('apply')
    setError(null)
    try {
      const r = await changeCreditedAmount(token, body())
      if (!r.data.success) {
        setError(settlementErrorFromBody(r.data, 'Не удалось изменить сумму'))
        return
      }
      onChanged(Number(r.data.data.newCreditedAmount ?? parseAmountInput(amount)))
      onClose()
    } catch (e: unknown) {
      setError(settlementErrorFromException(e, 'Не удалось изменить сумму'))
    } finally {
      setLoading(null)
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Изменить сумму зачисления</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-3 text-muted'>
          Сейчас зачислено: {currentAmount !== null ? formatSum(currentAmount) : '—'}
        </div>
        <div className='mb-3'>
          <label className='form-label required' htmlFor='credited-amount'>
            Сумма, которую должен получить мерчант
          </label>
          <input
            id='credited-amount'
            type='text'
            inputMode='decimal'
            className='form-control'
            autoComplete='off'
            value={amount}
            disabled={loading !== null}
            onChange={(e) => {
              if (AMOUNT_INPUT_PATTERN.test(e.target.value)) edit(() => setAmount(e.target.value))
            }}
          />
          <div className='form-text'>
            Уменьшение проводится как сторно, увеличение — как доначисление.
          </div>
        </div>
        <div className='mb-3'>
          <label className='form-label required' htmlFor='credited-amount-comment'>
            Комментарий
          </label>
          <textarea
            id='credited-amount-comment'
            className='form-control'
            rows={2}
            maxLength={1000}
            value={comment}
            disabled={loading !== null}
            onChange={(e) => edit(() => setComment(e.target.value))}
          />
        </div>
        {preview && <SettlementPreview preview={preview} />}
        {error && <div className='alert alert-danger mb-0'>{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button
          type='button'
          className='btn btn-light'
          onClick={onClose}
          disabled={loading !== null}
        >
          Отмена
        </button>
        <button
          type='button'
          className='btn btn-light-primary'
          onClick={showPreview}
          disabled={!filled || loading !== null}
        >
          {loading === 'preview' ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' />
              Считаем…
            </>
          ) : (
            'Показать проводки'
          )}
        </button>
        <button
          type='button'
          className='btn btn-primary'
          onClick={apply}
          disabled={!preview?.canApply || loading !== null}
        >
          {loading === 'apply' ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' />
              Проводим…
            </>
          ) : (
            'Провести'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default ChangeCreditedAmountModal
